
import { useEffect, useRef } from 'react';

interface UseGlobalScannerProps {
    onScan: (scanData: string) => void;
    minDuration?: number; // Minimum time between keypresses to consider it a scan (ms)
    enabled?: boolean;
}

export const useGlobalScanner = ({ onScan, enabled = true }: UseGlobalScannerProps) => {
    const buffer = useRef<string>('');
    const lastKeyTime = useRef<number>(0);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Configuration for scanner detection
    // Scanners typically type very fast (e.g., < 50ms between keys)
    const MAX_INTER_KEY_DELAY = 100;
    // Scanners end with Enter (KeyCode 13)

    useEffect(() => {
        if (!enabled) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Ignore if user is typing in an input field (unless strictly scanner mode)
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                // Optional: Decide if we want to allow scanning into inputs or capture globally
                // For now, let's respect focused inputs to avoid conflicts
                return;
            }

            const currentTime = Date.now();
            const isFastEnough = (currentTime - lastKeyTime.current) <= MAX_INTER_KEY_DELAY;

            // If delay is too long, reset buffer (assuming human typing)
            // Exception: First character of scan
            if (buffer.current.length > 0 && !isFastEnough) {
                buffer.current = '';
            }

            lastKeyTime.current = currentTime;

            if (e.key === 'Enter') {
                if (buffer.current.length > 2) { // Min length filter
                    e.preventDefault();
                    onScan(buffer.current);
                    buffer.current = '';
                } else {
                    buffer.current = '';
                }
            } else if (e.key.length === 1) { // Printable characters
                buffer.current += e.key;

                // Safety cleanup if no Enter received
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => {
                    buffer.current = '';
                }, MAX_INTER_KEY_DELAY * 2);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [enabled, onScan]);
};
